import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileLink } from "@/components/ProfileDrawer";
import { formatDateTimeWAT } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Siren } from "lucide-react";

const STATUSES = ["RECEIVED", "ASSIGNED", "EN_ROUTE", "ON_SCENE", "RESOLVED"];

export const Dispatch = () => {
  const [calls, setCalls] = useState([]);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ site_id: "", description: "", priority: "MEDIUM", assigned_guard_id: "" });

  const load = useCallback(async () => {
    const r = await api.get("/dispatch");
    setCalls(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/sites").then(r => setSites(r.data));
    api.get("/guards").then(r => setGuards(r.data));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/dispatch", { ...form, assigned_guard_id: form.assigned_guard_id || null, site_id: form.site_id || null });
      toast.success("Dispatch logged");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/dispatch/${id}/status`, { status });
    toast.success(`Status: ${status.replace("_", " ")}`);
    load();
  };

  const siteName = (id) => sites.find(s => s.id === id)?.name || "—";
  const guardName = (id) => guards.find(g => g.id === id)?.full_name || "Unassigned";

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{calls.length} dispatch calls logged</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-dispatch">
          <Plus size={14} /> New dispatch
        </button>
      </div>

      <div className="grid gap-3">
        {calls.map(c => (
          <div key={c.id} className="s-card" data-testid={`dispatch-${c.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-3">
                <Siren size={16} color={c.priority === "CRITICAL" || c.priority === "HIGH" ? "#E63946" : "#2ECAC8"} />
                <div>
                  <div style={{ fontSize: 15, color: "#fff" }}>{c.description}</div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {siteName(c.site_id)} · {c.assigned_guard_id ? <ProfileLink kind="guard" id={c.assigned_guard_id}>{guardName(c.assigned_guard_id)}</ProfileLink> : "Unassigned"} · {formatDateTimeWAT(c.received_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={c.priority} />
                <StatusBadge status={c.status} />
              </div>
            </div>
            {c.status !== "RESOLVED" && (
              <div className="flex flex-wrap gap-2 mt-3">
                {STATUSES.slice(STATUSES.indexOf(c.status) + 1).map(s => (
                  <button key={s} onClick={() => updateStatus(c.id, s)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 12 }}>
                    → {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card" style={{ width: 420, maxWidth: "90vw" }}>
            <div className="h-section mb-4">New dispatch call</div>
            <label className="text-muted" style={{ fontSize: 12 }}>Description</label>
            <textarea required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="s-input mt-1 mb-3" rows={3} />
            <label className="text-muted" style={{ fontSize: 12 }}>Site</label>
            <select value={form.site_id} onChange={(e) => setForm({...form, site_id: e.target.value})} className="s-input mt-1 mb-3">
              <option value="">No site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Priority</label>
            <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="s-input mt-1 mb-3">
              {["LOW","MEDIUM","HIGH","CRITICAL"].map(p => <option key={p}>{p}</option>)}
            </select>
            <label className="text-muted" style={{ fontSize: 12 }}>Assign guard</label>
            <select value={form.assigned_guard_id} onChange={(e) => setForm({...form, assigned_guard_id: e.target.value})} className="s-input mt-1 mb-4">
              <option value="">Unassigned</option>
              {guards.map(g => <option key={g.id} value={g.id}>{g.full_name}</option>)}
            </select>
            <button type="submit" className="btn-primary w-full">Log dispatch</button>
          </form>
        </div>
      )}
    </div>
  );
};
